import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/blacksmith-ui/button";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
interface GitHubRateLimitErrorProps {
  rateLimitReset: number;
  rateLimitResetSeconds: number | null;
  onRetry?: () => void;
}

/**
 * GitHub API rate-limit splash, styled as a pixel terminal: an HTTP `429`
 * display face, a Blacksmith-yellow accent rule, and a monospace `retry_after`
 * status line. Keeps the live countdown and the "retry disabled until reset"
 * behavior. Renders inside app chrome, so it keeps the existing `min-h-[60vh]`.
 */
export function GitHubRateLimitError(t0) {
  const $ = _c(26);
  const {
    rateLimitReset,
    rateLimitResetSeconds,
    onRetry
  } = t0;
  const [secondsRemaining, setSecondsRemaining] = useState(rateLimitResetSeconds);
  let t1;
  let t2;
  if ($[0] !== secondsRemaining) {
    t1 = () => {
      if (secondsRemaining === null || secondsRemaining <= 0) {
        return;
      }
      const interval = setInterval(() => {
        setSecondsRemaining(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    };
    t2 = [secondsRemaining];
    $[0] = secondsRemaining;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const formatTimeRemaining = _temp;
  let t3;
  if ($[3] !== rateLimitReset) {
    t3 = rateLimitReset ? new Date(rateLimitReset * 1000).toLocaleTimeString() : null;
    $[3] = rateLimitReset;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const resetTime = t3;
  const isUnknown = secondsRemaining === null;
  const isWaiting = secondsRemaining !== null && secondsRemaining > 0;
  const canRetry = secondsRemaining === 0 || isUnknown;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] opacity-60 [--pattern_color:var(--color-black)]/15 dark:[--pattern_color:var(--color-white)]/7" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="type-numerals-pixel-l text-neutral-900 dark:text-neutral-100">429</div>;
    t6 = <div className="mt-2 h-1 w-16 rounded-full bg-blacksmith-yellow" />;
    t7 = <h1 className="mt-8 type-page-title-m text-neutral-900 dark:text-neutral-100">GitHub API rate limit exceeded</h1>;
    t8 = <p className="mt-2 type-body-s-regular text-muted-foreground">This is your personal GitHub rate limit. Your GitHub access token has exceeded the API rate limit GitHub sets for your account, not Blacksmith. It usually clears on its own.</p>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isUnknown || $[11] !== isWaiting || $[12] !== resetTime || $[13] !== secondsRemaining) {
    t9 = isWaiting && secondsRemaining !== null ? <>retry_after: {formatTimeRemaining(secondsRemaining)}{resetTime && <span> · resets {resetTime}</span>}</> : isUnknown ? "retry_after: unknown" : "retry_after: 0s \xB7 ready";
    $[10] = isUnknown;
    $[11] = isWaiting;
    $[12] = resetTime;
    $[13] = secondsRemaining;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <p className="mt-3 type-code-block text-muted-foreground">{t9}</p>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== onRetry) {
    t11 = () => {
      if (onRetry) {
        onRetry();
      } else {
        window.location.reload();
      }
    };
    $[17] = onRetry;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = !canRetry;
  const t13 = canRetry ? "Try Again" : "Waiting...";
  let t14;
  if ($[19] !== t11 || $[20] !== t12 || $[21] !== t13) {
    t14 = <div className="mt-6"><Button icon={RefreshCw} onClick={t11} disabled={t12}>{t13}</Button></div>;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t14) {
    t15 = <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden bg-app-background p-6">{t4}<div className="relative flex max-w-md flex-col items-center text-center">{t5}{t6}{t7}{t8}{t10}{t14}</div></div>;
    $[23] = t10;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  return t15;
}
function _temp(seconds) {
  if (seconds <= 0) {
    return "now";
  }
  if (seconds < 60) {
    return `${seconds} seconds`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}